import axios from 'axios';
import { API_BASE } from './apiConfig';

const ADMIN = `${API_BASE}/api/admin`;

// ── Entities ──────────────────────────────────────────────────────────────────

export const getEntities = () =>
  axios.get(`${ADMIN}/entities`).then((r) => r.data);

export const createEntity = (name) =>
  axios.post(`${ADMIN}/entities`, { name }).then((r) => r.data);

// ── Spaces ────────────────────────────────────────────────────────────────────

export const getSpacesByEntity = (entityId) =>
  axios.get(`${ADMIN}/entities/${entityId}/spaces`).then((r) => r.data);

export const createSpace = (entityId, name, currencyId, locales) =>
  axios.post(`${ADMIN}/entities/${entityId}/spaces`, { entityId, name, currencyId, locales }).then((r) => r.data);

// ── Members ───────────────────────────────────────────────────────────────────

export const getSpaceMembers = (spaceId) =>
  axios.get(`${ADMIN}/spaces/${spaceId}/members`).then((r) => r.data);

// payload: { email, name?, roleId }
// Returns: { invited: bool, existingUser: bool }
export const inviteMember = (spaceId, payload) =>
  axios.post(`${ADMIN}/spaces/${spaceId}/members/invite`, payload).then((r) => r.data);

export const removeMember = (spaceId, userId) =>
  axios.delete(`${ADMIN}/spaces/${spaceId}/members/${userId}`).then((r) => r.data);

// payload: { roleIds: [1, 3] } — atomically replaces the member's full role set → 204
export const syncMemberRoles = (spaceId, userId, roleIds) =>
  axios.put(`${ADMIN}/spaces/${spaceId}/members/${userId}/roles`, { roleIds }).then((r) => r.data);

// payload: { isExcluded: bool } — excludes/includes this member from content-approval
// routing in this space only (other spaces they belong to are unaffected) → 204
export const setMemberApprovalExclusion = (spaceId, userId, isExcluded) =>
  axios.put(`${ADMIN}/spaces/${spaceId}/members/${userId}/approval-exclusion`, { isExcluded }).then((r) => r.data);

// Returns { userId, spaces: [{ spaceId, spaceName }] }
export const getUserSpaces = (spaceId, userId) =>
  axios.get(`${ADMIN}/spaces/${spaceId}/members/${userId}/spaces`).then((r) => r.data);

// Soft-deletes all space memberships + soft-deletes user account → 204
export const deleteUserAccount = (spaceId, userId) =>
  axios.delete(`${ADMIN}/spaces/${spaceId}/members/${userId}/account`).then((r) => r.data);

// Returns: [{ id, email, roleId, roleName, invitedByName, createdTime, expiresAt }]
export const getPendingInvitations = (spaceId) =>
  axios.get(`${ADMIN}/spaces/${spaceId}/members/pending-invitations`).then((r) => r.data);

// Soft-deletes a pending invitation → 204
export const revokePendingInvitation = (spaceId, invitationId) =>
  axios.delete(`${ADMIN}/spaces/${spaceId}/members/pending-invitations/${invitationId}`).then((r) => r.data);

// ── Roles ─────────────────────────────────────────────────────────────────────

export const getRoles = () =>
  axios.get(`${ADMIN}/roles`).then((r) => r.data);

// ── Entity Admin ──────────────────────────────────────────────────────────────

// Returns: [{ userId, name, email, entityId, assignedAt }]
export const getEntityAdmins = (entityId) =>
  axios.get(`${ADMIN}/entities/${entityId}/admins`).then((r) => r.data);

// Returns: [{ id, name, email, roleId, roleName, invitedByName, createdTime, expiresAt }]
export const getPendingEntityAdminInvitations = (entityId) =>
  axios.get(`${ADMIN}/entities/${entityId}/admins/pending-invitations`).then((r) => r.data);

// Soft-deletes a pending entity admin invitation → 204
export const revokePendingEntityAdminInvitation = (entityId, invitationId) =>
  axios.delete(`${ADMIN}/entities/${entityId}/admins/pending-invitations/${invitationId}`).then((r) => r.data);

// Strips entity_admin role → 200 { hasActiveSpaces: bool }
export const removeEntityAdmin = (entityId, userId) =>
  axios.delete(`${ADMIN}/entities/${entityId}/admins/${userId}`).then((r) => r.data);

// Permanently deletes a former entity admin account → 204
export const deleteEntityAdminAccount = (entityId, userId) =>
  axios.delete(`${ADMIN}/entities/${entityId}/admins/${userId}/account`).then((r) => r.data);

// payload: { email, name? }
// Returns: { invited: bool, existingUser: bool }
export const assignEntityAdmin = (entityId, payload) =>
  axios.post(`${ADMIN}/entities/${entityId}/admin`, payload).then((r) => r.data);

// ── GMS Accountant ────────────────────────────────────────────────────────────

// payload: { email, name? }
// Returns: { invited: bool, existingUser: bool }
export const inviteGmsAccountant = (payload) =>
  axios.post(`${ADMIN}/gms-accountant/invite`, payload).then((r) => r.data);

// Returns: [{ id, email, roleId, roleName, invitedByName, createdTime, expiresAt }]
export const getPendingGmsAccountantInvitations = () =>
  axios.get(`${ADMIN}/gms-accountant/pending-invitations`).then((r) => r.data);

// Soft-deletes a pending GMS accountant invitation → 204
export const revokeGmsAccountantInvitation = (invitationId) =>
  axios.delete(`${ADMIN}/gms-accountant/pending-invitations/${invitationId}`).then((r) => r.data);

// Returns: [{ userId, name, email, assignedAt }]
export const getGmsAccountants = () =>
  axios.get(`${ADMIN}/gms-accountant/list`).then((r) => r.data);

// Strips gms_accountant role → 200 { hasActiveSpaces: bool }
export const removeGmsAccountant = (userId) =>
  axios.delete(`${ADMIN}/gms-accountant/${userId}`).then((r) => r.data);

// Permanently deletes a former GMS accountant account → 204
export const deleteGmsAccountantAccount = (userId) =>
  axios.delete(`${ADMIN}/gms-accountant/${userId}/account`).then((r) => r.data);
